import SaveIcon from "@mui/icons-material/Save";
import { Collapse, Fab, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthProvider } from "../../auth/hooks/auth-provider";
import SessionTimeoutDialog from "../../components/freelancer/onboarding/components/common/SessionTimeoutDialog";
import UnsavedChangesDialog from "../../components/freelancer/onboarding/components/common/UnsavedChangesDialog";
import FreelancerOnboardingMyAvailability from "../../components/freelancer/onboarding/FreelancerOnboardingMyAvailability";
import FreelancerOnboardingMyExpertise from "../../components/freelancer/onboarding/FreelancerOnboardingMyExpertise";
import FreelancerOnboardingMyPersonalInformation from "../../components/freelancer/onboarding/FreelancerOnboardingMyPersonalInformation";
import FreelancerOnboardingMyPresentation from "../../components/freelancer/onboarding/FreelancerOnboardingMyPresentation";
import FreelancerOnboardingMyProfessionalInformation from "../../components/freelancer/onboarding/FreelancerOnboardingMyProfessionalInformation";
import FreelancerOnboardingMyProfile from "../../components/freelancer/onboarding/FreelancerOnboardingMyProfile";
import FreelancerOnboardingMySectors from "../../components/freelancer/onboarding/FreelancerOnboardingMySectors";
import FreelancerOnboardingMySkills from "../../components/freelancer/onboarding/FreelancerOnboardingMySkills";
import { useFreelancerContext } from "../../contexts/useFreelancerContext";
import { useSnackbarContext } from "../../contexts/useSnackBarContext";
import { useFreelancerOnboardingFormSave } from "../../hooks/useFreelancerOnboardingFormsSave";
import { useFreelancerOnboardingFormsUnsavedChanges } from "../../hooks/useFreelancerOnboardingFormsUnsavedChanges";
import { FreelancerOnboardingFormStep } from "../../models/enums/FreelancerOnboardingFormStep";
import { FreelancerOnboardingStatus } from "../../models/enums/FreelancerOnboardingStatus";
import { UserRole } from "../../models/UserRole";

const FreelancerOnboardingForms: React.FC = () => {
	const { currentOnboardingFormStep, freelancerProfileQueryResult } =
		useFreelancerContext();
	const { saveOnboardingFormWithoutValidationAsync, isSaveProgressPending } =
		useFreelancerOnboardingFormSave();
	const { hasUnsavedChanges } = useFreelancerOnboardingFormsUnsavedChanges();
	const { showError } = useSnackbarContext();
	const [showAnimatedText, setShowAnimatedText] = useState(false);
	const { t } = useTranslation();
	const { user } = useAuthProvider();
	const isTam = user.roles?.includes(UserRole.TAM) || false;

	useEffect(() => {
		setShowAnimatedText(
			currentOnboardingFormStep !== FreelancerOnboardingFormStep.MyProfile
		);

		const timer = setTimeout(() => {
			setShowAnimatedText(false);
		}, 4000);

		return () => clearTimeout(timer);
	}, [currentOnboardingFormStep]);

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (hasUnsavedChanges) {
				event.preventDefault();
				return "";
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [hasUnsavedChanges]);

	const renderStep = () => {
		switch (currentOnboardingFormStep) {
			case FreelancerOnboardingFormStep.MyProfile:
				return <FreelancerOnboardingMyProfile />;
			case FreelancerOnboardingFormStep.MyExpertise:
				return <FreelancerOnboardingMyExpertise />;
			case FreelancerOnboardingFormStep.MySkills:
				return <FreelancerOnboardingMySkills />;
			case FreelancerOnboardingFormStep.MySectors:
				return <FreelancerOnboardingMySectors />;
			case FreelancerOnboardingFormStep.MyPresentation:
				return <FreelancerOnboardingMyPresentation />;
			case FreelancerOnboardingFormStep.MyAvailability:
				return <FreelancerOnboardingMyAvailability />;
			case FreelancerOnboardingFormStep.MyProfessionalInformation:
				return <FreelancerOnboardingMyProfessionalInformation />;
			case FreelancerOnboardingFormStep.MyPersonalInformation:
				return <FreelancerOnboardingMyPersonalInformation />;
			default:
				return <FreelancerOnboardingMyProfile />;
		}
	};

	return (
		<>
			{renderStep()}
			{currentOnboardingFormStep !== FreelancerOnboardingFormStep.MyProfile && (
				<>
					{(isTam ||
						freelancerProfileQueryResult.data?.onboarding.currentStatus !==
							FreelancerOnboardingStatus.Accepted) && (
						<Fab
							disableRipple
							color="secondary"
							variant="extended"
							disabled={isSaveProgressPending}
							onClick={async () => {
								try {
									await saveOnboardingFormWithoutValidationAsync(
										currentOnboardingFormStep,
										currentOnboardingFormStep
									);
								} catch (error) {
									// The save hook rethrows so NAVIGATION callers can block; here
									// there is no navigation at stake — tell the user their save
									// failed (translation-save errors show no snackbar of their own).
									console.error("Error saving onboarding progress:", error);
									showError("common.saveRetryError");
								}
							}}
							sx={{
								position: "fixed",
								bottom: "100px",
								right: "40px",
								zIndex: 1000,
								"&:hover": {
									transform: "scale(1.05)"
								},
								minWidth: "56px",
								height: "56px",
								overflow: "hidden",
								transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
							}}
						>
							<SaveIcon
								sx={{
									mr: showAnimatedText ? 1 : 0,
									transition: "margin-right 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
								}}
							/>
							<Collapse
								in={showAnimatedText}
								orientation="horizontal"
								timeout={500}
								sx={{
									"& .MuiCollapse-wrapperInner": {
										whiteSpace: "nowrap"
									}
								}}
							>
								<Typography variant="body2" sx={{ fontWeight: 500 }}>
									{t("freelancerOnboardingForms.saveProgressFloatingButton")}
								</Typography>
							</Collapse>
						</Fab>
					)}
					<UnsavedChangesDialog />
					<SessionTimeoutDialog />
				</>
			)}
		</>
	);
};

export default FreelancerOnboardingForms;
